import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import moment from "moment";
import { from, Subscription } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { UserAuthenticationSettings } from "src/model/class/general classes/userAuthenticationSettings";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { TrialFlag, UsersRole } from "src/model/enum/enumDigitalPlace";
import { MomentKey } from "src/model/enum/enumMoment";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iCredential } from "src/model/interface/icredential";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iRefreshTokenData } from "src/model/interface/iRefreshTokeData";
import {
  currentUserSetAction,
  currentUserUnsetAction,
} from "src/state/actions/authentication.actions";
import { storageUnsetAllAuthKeyAction } from "src/state/actions/local-storage.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoaderService } from "./loader.service";
import { LoggerService } from "./logger.service";
import { SecuretyDataProtectionService } from "./securety-data-protection.service";
import { SettingsService } from "./settings.service";
import { SystemRouterService } from "./system-router.service";
import { ThemeService } from "./theme.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

/**
 * This service handle digital place authentication system
 */
@Injectable({
  providedIn: "root",
})
export class DpAuthenticationService implements OnDestroy, iEvent {
  private _selectCurrentUserSub: Subscription = null;

  private _userStore: CurrentSessionData = null;

  private _logContext: string = "Dp-authentication.service";

  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _eventService: EventService,
    private _systemRouterService: SystemRouterService,
    private _alertService: AlertService,
    private _loaderController: LoaderService,
    private _settingService: SettingsService,
    private _themeService: ThemeService
  ) {
    this.loadCurrentUser();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
  }

  /**
   * Handle base login for digital place account
   *
   * @param userCredentials User credentials account
   * @returns
   */
  public async login(userCredentials: iCredential): Promise<unknown> {
    const encryptedCredentials = this._securetyDataProtectionService.encrypt(
      JSON.stringify(userCredentials)
    );

    return this._http
      .post(
        environment.httpDPAPIRoot +
        environment.anaciPlatformAuth +
        AppSettings.API_URL_ANACI_AUTH +
        "/login",
        { credentials: encryptedCredentials }
      )
      .pipe(
        tap((authResponse: UserAuthenticationSettings) => {
          const currentSession: CurrentSessionData =
            this.createUserSessionFromAuthResponse(
              authResponse,
              userCredentials
            );

          this.setUserData(currentSession);

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "login()",
            "Login completed for user id: " + authResponse.id
          );
        }),
        switchMap(() => this._settingService.getSettings())
      )
      .toPromise();
  }

  /**
   * Perform logout operations from digital place platform
   *
   * @returns
   */
  public async logout(doLogoutApiCall: boolean = true): Promise<void> {
    /** AB: after refresh token expiration, if you click on a supervision, loader is going to be loaded
     *    and stay locked on the screen; this command dismiss the loader (inside check if it exist) */
    await this._loaderController.dismissLoaderWaiting();

    try {
      if (doLogoutApiCall) {
        await this._http
          .post(
            environment.httpDPAPIRoot +
            environment.anaciPlatformAuth +
            AppSettings.API_URL_ANACI_AUTH +
            "/logout",
            {}
          )
          .toPromise();
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "logout()",
        "Error in logout API call: " + (error?.message ?? JSON.stringify(error))
      );
    } finally {
      await this.performLogout();
    }
  }

  /*
public async logout(): Promise<unknown> {
 
 return this._http
   .post(
     environment.httpDPAPIRoot +
       AppSettings.API_URL_ANACI_PLATFORM +
       AppSettings.API_URL_ANACI_AUTH +
       "/logout",
     {}
   )
   .toPromise();

}
*/

  /**
   * Perform refresh token procedure
   *
   * @returns
   */
  public async getRefreshToken(): Promise<any> {
    const refreshTokenObject: Partial<iRefreshTokenData> = {
      refreshToken: this._userStore?.refreshToken,
    };

    return this._http
      .post(
        environment.httpDPAPIRoot +
        environment.anaciPlatformAuth +
        AppSettings.API_URL_ANACI_AUTH +
        "/refreshtoken",
        refreshTokenObject
      )
      .toPromise();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this.performLogout();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *  {@link performLogout} execute logout operation:
   *
   *  - Unset all storage key
   *  - Unset current session data
   *  - Propagate logout event to subscrie component/service/page
   *  - Redirect to homepage
   *  - Propagate the end of the logout (MQTT is disconnected here)
   */
  private async performLogout(): Promise<void> {
    this.unsetCurrentUserData();
    this._eventService.OnMakeLogout.next(null);

    await this.closeAllAlert();

    /* The end of the logout is signalled once the pages of the reserved area are destroyed and have released
     their MQTT topics: closing the connection earlier made each late release fail with "client disconnecting". */
    const leavingPagesDestroyedSub: Subscription =
      this._systemRouterService.leavingPagesDestroyed$
        .pipe(take(1))
        .subscribe(() => this._eventService.OnLogoutCompleted.next());

    const hasLeftPage: boolean =
      await this._systemRouterService.navigateToHomepage();

    if (!hasLeftPage) {
      leavingPagesDestroyedSub.unsubscribe();
      this._eventService.OnLogoutCompleted.next();
    }

    //COMMENTED FOR NOW, BUT ULTIMATELY NECESSARY.
    /*
    await this._toastService.closeToastConnectionIssues(
      ToastConnectionCodes.MQTT_CONNECTION_REGAIN
    );
    await this._toastService.closeToastConnectionIssues(
      ToastConnectionCodes.API_CONNECTION_REGAIN
    );
    */

    //this._systemRouterService.navigateToDigitalPlaceLogin();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "performLogout()",
      "Logout completed"
    );
  }

  /**
   * Close all open popup
   */
  private async closeAllAlert(): Promise<void> {
    await this._alertService.clearAlertOk();
    await this._alertService.clearAlertDisconnection();
    await this._alertService.clearAlertIpNotAllowed();
    await this._alertService.clearAlertCommandNotAvaiable();
  }

  /**
   * Unset current user data
   */
  public unsetCurrentUserData(): void {
    try {
      this._store.dispatch(currentUserUnsetAction());
      this._store.dispatch(storageUnsetAllAuthKeyAction());
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "unsetCurrentUserData()",
        "Error unsetting current user data: " +
          (exception?.message ?? JSON.stringify(exception))
      );
    }
  }

  /**
   * Create user sessiont object from authentication response and user credentials
   *
   * @param authResponse Authentication response received from API
   * @param userCredential User credentials object
   * @returns
   */
  private createUserSessionFromAuthResponse(
    authResponse: UserAuthenticationSettings,
    userCredential: iCredential
  ): CurrentSessionData {
    const currentSession = new CurrentSessionData();
    currentSession.id = authResponse.id;
    currentSession.username = this._securetyDataProtectionService.encrypt(
      userCredential.Username
    );
    currentSession.password = this._securetyDataProtectionService.encrypt(
      userCredential.Password
    );

    currentSession.rememberCredentials = userCredential.RememberMe;
    currentSession.isAutoLoginOn = userCredential.isAutoLoginOn;

    /* NOTE: If the "AutoLogin" function has been selected, THEN ALSO the "Remenber Credentials" function is considered active*/
    if (currentSession.isAutoLoginOn) {
      currentSession.rememberCredentials = currentSession.isAutoLoginOn;
    }

    currentSession.accessToken = authResponse.accessToken;
    currentSession.refreshToken = authResponse.refreshToken;

    const expireTokenAccessDateUTC = moment()
      .utc()
      .add(authResponse.expirationAccessTokenTime, MomentKey.MINUTES);
    currentSession.expireTokenAccessDate = expireTokenAccessDateUTC.valueOf();
    currentSession.expireTokenAccessDateString =
      expireTokenAccessDateUTC.format(MomentFormats.MOMENT_DATETIME_FORMAT);

    const expireTokenRefreshDateUTC = moment()
      .utc()
      .add(authResponse.expirationRefreshTokenTime, MomentKey.MINUTES);
    currentSession.expireTokenRefreshDate = expireTokenRefreshDateUTC.valueOf();
    currentSession.expireTokenRefreshDateString =
      expireTokenRefreshDateUTC.format(MomentFormats.MOMENT_DATETIME_FORMAT);
    currentSession.role = authResponse?.role;
    currentSession.name = authResponse?.name;
    currentSession.surname = authResponse?.surname;
    currentSession.commercialComunication =
      authResponse?.commercialComunication;
    currentSession.personalizingService = authResponse?.personalizingService;
    currentSession.idUserRemote = authResponse?.idUserRemote;
    currentSession.trial = authResponse?.trial;
    currentSession.expirationDate = authResponse?.expirationDate;
    return currentSession;
  }

  /**
   * Return how many days are left before the trial period of the currently
   * logged user expires.
   *
   * The value is returned ONLY when the warning has to be shown, this means when
   * ALL these conditions are satisfied:
   *
   * * the logged user is a building administrator
   * * the "trial" flag received on login is set to "S"
   * * the trial expiration date is not reached yet
   *
   * @returns Days left before the trial expiration (0 means "it expires today"),
   *          NULL when no warning has to be shown
   */
  public getTrialDaysLeft(): number | null {
    if (this._userStore?.role !== UsersRole.BUILDING_ADMINISTRATOR) {
      return null;
    }

    if (this._userStore?.trial?.toUpperCase() !== TrialFlag.ACTIVE) {
      return null;
    }

    const expirationDate = this.parseTrialExpirationDate(
      this._userStore?.expirationDate
    );

    if (!expirationDate?.isValid()) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "getTrialDaysLeft()",
        "Unable to parse trial expiration date: " +
          this._userStore?.expirationDate
      );

      return null;
    }

    const daysLeft: number = expirationDate
      .startOf(MomentKey.DAYS)
      .diff(moment().startOf(MomentKey.DAYS), MomentKey.DAYS);

    /* Trial period ALREADY expired: the warning is not shown */
    return daysLeft >= 0 ? daysLeft : null;
  }

  /**
   * Parse the trial expiration date received from the login response.
   *
   * The date is parsed against the formats used by the API, so that both the ISO
   * format and the italian culture-info one are supported.
   *
   * @param expirationDate Expiration date as received from the API
   * @returns Parsed moment object, NULL when no date has been received
   */
  private parseTrialExpirationDate(expirationDate: string): moment.Moment {
    if (!expirationDate) {
      return null;
    }

    return moment(expirationDate, [
      moment.ISO_8601,
      MomentFormats.MOMENT_DATETIME_FORMAT,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO,
      MomentFormats.MOMENT_DATETIME_FORMAT_DATE_YEAR,
    ]);
  }

  /**
   *  {@link setUserData} save current user data into local storage and propagate it to all subscribe component/service/page
   *
   *  @param userLogged current user data
   */
  private setUserData(userLogged: CurrentSessionData): void {
    this._store.dispatch(currentUserSetAction({ currentUser: userLogged }));
  }

  /**
   * Load current user from fstore
   */
  private loadCurrentUser(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        map((currentUserData: GenericUserData) => currentUserData?.currentUser)
      )
      .subscribe((currentDPUser: CurrentSessionData) => {
        this._userStore = currentDPUser;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }
  }
  //#endregion
}
